import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormText,
  Input,
  Label,
  Progress,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { TiCancel } from "react-icons/ti";
import { CiSquareRemove } from "react-icons/ci";
import MP4Box from "mp4box";
import axios from "axios";
import api from "../../api/api";
import { useParams } from "react-router-dom";
import { WarningModal } from "../WarningModal/WarningModal";
import useSubtitleValidationStore from "../../store/useSubtitleValidationStore";

const chunkSize = 100000000;

const VideoCardNewMission = forwardRef(
  ({ video, registerCancelToken, removeVideo }, ref) => {
    const [axiosCancelToken, setAxiosCancelToken] = useState(
      axios.CancelToken.source()
    );

    const { startValidating, finishValidating } = useSubtitleValidationStore();

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState();

    const [subtitle, setSubtitle] = useState(null);
    const [videoHasSubtitles, setVideoHasSubtitles] = useState(true);
    const [loadingSubtitles, setLoadingSubtitles] = useState(false);

    const [uploaded, setUploaded] = useState(false);
    const [beingUploaded, setBeingUploaded] = useState(false);
    const [progressBar, setProgressBar] = useState(0);
    const [modal, setModal] = useState();

    let { id: projectId } = useParams();

    useEffect(() => {
      registerCancelToken(axiosCancelToken);

      const validateSubtitle = async () => {
        let hasSubtitles = true;

        if (isMp4File(video)) {
          try {
            startValidating(video.name);
            setLoadingSubtitles(true);
            hasSubtitles = await checkMP4ForSubtitles(video);
            setVideoHasSubtitles(hasSubtitles);
          } catch (error) {
            console.error("Error checking subtitles:", error);
          } finally {
            setLoadingSubtitles(false);
            finishValidating(video.name)
          }
        }
      };

      if (video) {
        validateSubtitle();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
      upload: async () => {
        if (!videoHasSubtitles && !loadingSubtitles && !subtitle) {
          return;
        }
        return handleUpload();
      },
      uploaded() {
        return uploaded && (videoHasSubtitles || subtitle);
      },
    }));

    async function createMissionAndUploadVideo(missionName) {
      const { data } = await api.post(
        "/missions",
        {
          name: missionName,
          project_id: projectId,
        }
      );

      return await uploadVideo(video, data.id);
    }

    async function handleUpload() {
      if (uploaded) return;

      let missionName = video.name.split(".")[0];
      let success = false;

      try {
        setLoading(true);

        while (!success) {
          try {
            await createMissionAndUploadVideo(missionName);
            success = true;
          } catch (e) {
            setBeingUploaded(false);
            console.error(e);

            if (e.response?.data === "MISSION_NAME_ALREADY_EXISTS") {
              // eslint-disable-next-line no-loop-func
              const userResponse = await new Promise((resolve) => {
                setModal({
                  show: true,
                  title: "Missão já existente!",
                  message: `Já existe uma missão com o nome ${missionName}.
                    Por favor, insira um novo nome para a missão!`,
                  buttonText: "Enviar",
                  input: true,
                  onConfirm: (newName) => {
                    resolve({ action: "retry", newName });
                    setModal({ show: false });
                  },
                  onCancel: () => {
                    resolve({ action: "cancel" });
                    setModal({ show: false });
                  },
                });
              });

              if (userResponse.action === "cancel") break;
              missionName = userResponse.newName;
            } else {
              setMessage("Erro ao criar missão.");
              break;
            }
          }
        }
      } finally {
        setLoading(false);
      }
    }

    const isMp4File = (file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension === "mp4") {
        return true;
      }
      return false;
    };

    const checkMP4ForSubtitles = (file) => {
      return new Promise((resolve, reject) => {
        const mp4boxFile = MP4Box.createFile();
        const chunkSize = 1024 * 1024;
        let offset = 0;

        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          const arrayBuffer = e.target.result;
          arrayBuffer.fileStart = offset;
          mp4boxFile.appendBuffer(arrayBuffer);
          offset += arrayBuffer.byteLength;

          if (offset < file.size) {
            readNextChunk();
          } else {
            mp4boxFile.flush();
          }
        };

        mp4boxFile.onReady = (info) => {
          // Check for subtitle/text tracks
          const hasDroneSubtitleTrack = info.tracks.some(
            (track) =>
              // Detect subtitle-type tracks or DJI embedded metadata
              ["text", "tx3g", "wvtt", "stpp"].includes(
                track.codec?.toLowerCase()
              ) || track.name?.toLowerCase().includes("dji.subtitle")
          );

          resolve(hasDroneSubtitleTrack);
        };

        mp4boxFile.onError = (e) => reject(e);

        const readNextChunk = () => {
          const slice = file.slice(offset, offset + chunkSize);
          fileReader.readAsArrayBuffer(slice);
        };

        readNextChunk();
      });
    };

    const uploadVideo = async (video, missionId) => {
      let folderName = null;
      let fileKey = null;
      let uploadId = null;

      const uploadFilesToPresignedUrl = async () => {
        try {
          // 1. First upload all videos
          const response = await startMultipartUpload(
            video,
            folderName,
            missionId
          );

          fileKey = response.fileKey;
          uploadId = response.uploadId;
          folderName = response.folderName;

          const presignedURLs = response.presignedURLs;

          const partETags = await uploadFileParts(presignedURLs, video);

          await completeMultipartUpload(
            fileKey,
            uploadId,
            partETags,
            missionId
          );

          if (subtitle) {
            const response = await startMultipartUpload(
              subtitle,
              folderName,
              missionId
            );

            fileKey = response.fileKey;
            uploadId = response.uploadId;

            const presignedURLs = response.presignedURLs;

            const partETags = await uploadFileParts(presignedURLs, subtitle);

            await completeMultipartUpload(
              fileKey,
              uploadId,
              partETags,
              missionId
            );
          }

          startPipeline(folderName, missionId);
          setMessage("Os arquivos foram enviados.");
          setUploaded(true);
        } catch (error) {
          deleteMission(missionId);

          if (error.code === "ERR_CANCELED") {
            await abortMultiparUpload(fileKey, uploadId, missionId);
            setMessage("Upload cancelado pelo usuário");
            return;
          }

          setMessage(error.message);
        } finally {
          setAxiosCancelToken(axios.CancelToken.source());
          setTimeout(() => {
            setProgressBar(0);
          }, 2000);
          setBeingUploaded(false);
        }
      };

      setBeingUploaded(true);
      await uploadFilesToPresignedUrl();
    };

    const startMultipartUpload = async (file, folderName, missionId) => {
      let totalSize = file.size;
      let numOfParts = Math.ceil(totalSize / chunkSize);

      const response = await api.post(
        `/missions/${missionId}/images/upload-video/start`,
        {
          fileName: file.name,
          folderName: folderName,
          numberOfParts: numOfParts,
        },
        {
          cancelToken: axiosCancelToken.token,
        }
      );

      return response.data;
    };

    const uploadFileParts = async (presignedURLs, file) => {
      let partETags = [];

      for (let i = 0; i < presignedURLs.length; i++) {
        const url = presignedURLs[i];
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const uploadResponse = await axios.put(url, chunk, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          cancelToken: axiosCancelToken.token,
          onUploadProgress(progressEvent) {
            const percentageCompleted =
              progressEvent.loaded / progressEvent.total;

            const currentPartProgress =
              (i + percentageCompleted) / presignedURLs.length;

            setProgressBar(currentPartProgress);
          },
        });

        partETags.push({
          eTag: uploadResponse.headers.etag,
          partNumber: i + 1,
        });
      }

      return partETags;
    };

    const completeMultipartUpload = async (
      fileKey,
      uploadId,
      partETags,
      missionId
    ) => {
      const response = await api.post(
        `/missions/${missionId}/images/upload-video/finish`,
        {
          fileKey,
          uploadId,
          partETags,
        },
        {
          cancelToken: axiosCancelToken.token,
        }
      );

      return response.data;
    };

    const startPipeline = async (folderName, missionId) => {
      await api.post(
        `/missions/${missionId}/images/upload-video/start-pipeline`,
        {
          folderName: folderName,
        }
      );
    };

    const abortMultiparUpload = async (fileKey, uploadId, missionId) => {
      await api.post(`/missions/${missionId}/images/upload-video/abort`, {
        fileKey: fileKey,
        uploadId: uploadId,
      });
    };

    const handleSubtitle = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const removeExtension = (filename) => {
        return filename.replace(/\.[^/.]+$/, "");
      };

      const videoNameWithoutExt = removeExtension(video.name);
      const subtitleNameWithoutExt = removeExtension(file.name);

      if (file && videoNameWithoutExt === subtitleNameWithoutExt) {
        setSubtitle(file);
        return;
      }

      setModal({
        show: true,
        title: "Nomes Diferentes!",
        message: `A legenda "${subtitleNameWithoutExt}" não corresponde ao vídeo "${videoNameWithoutExt}". Deseja continuar?`,
        buttonText: "Continuar",
        onConfirm: () => {
          setSubtitle(file);
        },
        onCancel: () => {
          e.target.value = "";
          setSubtitle()
          setModal({ show: false });
        },
      });
    };

    const deleteMission = async (id) => {
      try {
        await api.delete(`/missions/${id}`);
      } catch (error) {
        console.error("Error deleting mission:", error);
      }
    };

    return (
      <>
        {modal && <WarningModal modal={modal} setModal={setModal} />}

        <Card>
          <CardHeader
            style={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div style={{ alignItems: "center", display: "flex", gap: "1rem" }}>
              {loading && <Spinner style={{ width: "15px", height: "15px" }} />}
              <span>{video.name}</span>
            </div>

            <CiSquareRemove
              id="removeButton"
              style={{ cursor: "pointer", fontSize: "2rem", color: "red" }}
              onClick={() => {
                removeVideo(video.name);
              }}
            />

            <UncontrolledTooltip placement="top" target="removeButton">
              Remover
            </UncontrolledTooltip>
          </CardHeader>

          <CardBody style={{ padding: ".5rem" }}>
            {message && <div className="mt-2">{message}</div>}

            {loadingSubtitles && (
              <div className="mt-2 text-info">
                Verificando legendas nos vídeos...
              </div>
            )}

            {!videoHasSubtitles && !loadingSubtitles && (
              <div>
                <Label for="fileSubtitle">Arquivo(s) com dados de GPS</Label>
                <Input
                  id="fileSubtitle"
                  name="subtitle"
                  type="file"
                  required
                  accept=".srt,.csv,.gpx"
                  onChange={handleSubtitle}
                />
                <FormText>Selecione o arquivo a ser enviado.</FormText>

                {!beingUploaded && (
                  <Button
                    outline
                    className="col-12 mt-3"
                    disabled={!subtitle}
                    onClick={() => handleUpload()}
                  >
                    Enviar
                  </Button>
                )}
              </div>
            )}

            {beingUploaded && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <Progress
                  className="mt-2 mb-2 w-100"
                  style={{ height: "20px" }}
                  color="success"
                  value={progressBar ? progressBar * 100 : 0}
                >
                  {progressBar ? `${(progressBar * 100).toFixed(1)}%` : "0%"}
                </Progress>
                <Button
                  id="cancelButton"
                  outline
                  onClick={() => {
                    setProgressBar(0);
                    axiosCancelToken.cancel("Upload canceled by user!");
                  }}
                >
                  <TiCancel />
                </Button>

                <UncontrolledTooltip placement="top" target="cancelButton">
                  Cancelar Envio
                </UncontrolledTooltip>
              </div>
            )}
          </CardBody>
        </Card>
      </>
    );
  }
);

export default VideoCardNewMission;
